import { autoinject } from 'aurelia-dependency-injection';
import { Subscription } from 'aurelia-event-aggregator';
import { bindable, BindingEngine, computedFrom } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ClassicPanelDialogUtils } from '~/components/classic-panel/classic-panel-dialog-utils';
import { CustomisationConfig } from '~/components/customisation/customisation-config';
import {
	DynamicFieldMeta,
	DynamicFieldTable,
} from '~/components/dynamic-field-table/dynamic-field-table';
import {
	FormModelListFieldTable,
	MoveRecordDirection,
} from '~/components/dynamic-field-table/form-model-list-field-table';
import {
	SearchFieldModel,
	SearchType,
} from '~/components/field/common/search-definition';
import { FilterType } from '~/components/filter/filter-options';
import { BatchProcessFieldName } from '~/models/entities/batch-process';
import { FieldModel, FormModel, Modifiable } from '~/models/form-model';
import { MemberPaymentHistoryField } from '~/models/generated/entities/MemberPaymentHistory';
import FormModelUtils from '~/utils/form-model-utils';

@autoinject
export abstract class AbstractPaymentItemsList {
	// Maximum number of rows for either Dispersal Defaults OR
	// Payment Details is 9
	protected static readonly MAX_ROWS_SIZE: number = 9;
	// 9 for Dispersals and 9 for Payment Details
	private readonly FORM_MODEL_LIST_MAX_SIZE: number = 18;
	// module parameters
	@bindable protected functionName: string;
	@bindable protected customisationConfig: CustomisationConfig;
	@bindable({
		changeHandler: 'formModelChanged',
	})
	protected formModel: FormModel;
	// dynamic labels
	protected headerClassName: string;
	protected headerText: string;
	protected addRecordButtonTitle: string;
	// header button visibilities
	protected get isListEditable(): boolean {
		return false;
	}
	protected get renderAddRecord(): boolean {
		return false;
	}
	protected get renderToggleTotalMethod(): boolean {
		return false;
	}
	protected get renderTogglePreservedMethod(): boolean {
		return false;
	}
	// list fields change handlers
	protected messageSubscriptions: Subscription[] = [];
	private payBySubscriptions: Subscription[] = [];
	// miscellaneous fields
	protected calculateGlobalRowIndex = false;
	protected forceSaveFields = [];
	protected selectedConversationIds: string[] = [];
	protected uniqueFieldMeta: string;
	protected abstract onOptionalItemsButtonClick: (
		rowNum: number,
		totalRows: number,
	) => void;
	protected abstract onSelectConvID: (
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	) => void;
	protected abstract onSelectPayee: (
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	) => void;

	constructor(
		protected readonly i18n?: I18N,
		protected readonly classicPanelDialogUtils?: ClassicPanelDialogUtils,
		protected bindingEngine?: BindingEngine,
		...rest
	) {
		// these fields must be saved even though they are not visible on form
		this.forceSaveFields = [
			MemberPaymentHistoryField.PYz_Fund,
			MemberPaymentHistoryField.PYz_Member,
			MemberPaymentHistoryField.PYd_Effective,
			MemberPaymentHistoryField.PYz_OrgName,
			MemberPaymentHistoryField.PYz_Aust_Business_No,
			MemberPaymentHistoryField.PYz_ProdID,
			MemberPaymentHistoryField.PYz_MemberID,
			MemberPaymentHistoryField.PYz_Dest_Mbr_Acc_No,
			MemberPaymentHistoryField.PYz_Address_Line02,
			MemberPaymentHistoryField.PYz_Address_Line03,
			MemberPaymentHistoryField.PYz_Suburb,
			MemberPaymentHistoryField.PYz_State,
			MemberPaymentHistoryField.PYz_Post_Code,
			MemberPaymentHistoryField.PYz_Country_Code,
			MemberPaymentHistoryField.PYz_BSB,
			MemberPaymentHistoryField.PYz_Bank_Acct_Name,
			MemberPaymentHistoryField.PYz_Bank_Acct_No,
			MemberPaymentHistoryField.PYz_Reference,
			MemberPaymentHistoryField.PYz_Reversion_Client,
			MemberPaymentHistoryField.PYl_Policy_Term,
			MemberPaymentHistoryField.PYz_Dep_Reference,
			MemberPaymentHistoryField.PYz_Transfer_Type,
			MemberPaymentHistoryField.PYz_PaymentReference,
			PaymentItemField.PRIORITY,
			PaymentItemField.MS_REC_NO,
		];
	}

	@computedFrom('uniqueFieldMeta')
	protected get listFieldMeta(): DynamicFieldMeta[] {
		return [
			{
				fieldNamePrefix: this.uniqueFieldMeta,
				fieldType: 'text',
				hidden: true,
			},
			{
				fieldNamePrefix: PaymentItemField.MSG,
				fieldType: 'select',
			},
			{
				fieldNamePrefix: PaymentItemField.CONV_ID,
				fieldType: 'search',
				onSearchSelect: this.onSelectConvID,
			},
			{
				fieldNamePrefix: PaymentItemField.PAYEE,
				fieldType: 'search',
				button: true,
				buttonImageClass: 'fa-pencil-alt',
				onSearchSelect: this.onSelectPayee,
				onButtonClick: this.onOptionalItemsButtonClick,
			},
			{
				fieldNamePrefix: PaymentItemField.TOTAL_VALUE,
				fieldType: 'number',
			},
			{
				fieldNamePrefix: PaymentItemField.PRESERVED_VALUE,
				fieldType: 'number',
			},
			{
				fieldNamePrefix: PaymentItemField.PAY_BY,
				fieldType: 'select',
			},
		];
	}

	protected formModelChanged(formModel: FormModel, oldFormModel: FormModel) {
		if (!formModel || !formModel.formModelList) {
			return;
		}
		AbstractPaymentItemsList.removeSubscriptions(this.messageSubscriptions);
		AbstractPaymentItemsList.removeSubscriptions(this.payBySubscriptions);
		AbstractPaymentItemsList.setParentForceSaveFields(formModel);

		formModel.formModelList.forEach(
			(formModelItem: FormModel, rowIndex) => {
				const suffix = DynamicFieldTable.getSuffix(rowIndex + 1);
				if (this.isPaymentItemPartOfList(formModelItem, suffix)) {
					this.formModelChangedListItemProcessing(
						formModelItem,
						formModel,
						rowIndex,
						suffix,
					);
				}
			},
		);
	}

	protected formModelChangedListItemProcessing(
		formModelItem: FormModel,
		parentFormModel: FormModel,
		rowIndex,
		suffix,
	) {
		// Payee setup
		const payeeFormModel: SearchFieldModel =
			formModelItem.fields[PaymentItemField.PAYEE + suffix];
		if (payeeFormModel) {
			AbstractPaymentItemsList.setupPayeeSearchField(payeeFormModel);
		}
		// Add subscription to pay-by field
		AbstractPaymentItemsList.addPayBySubscription(
			formModelItem,
			suffix,
			this.bindingEngine,
			this.payBySubscriptions,
		);
		// Set force save fields
		AbstractPaymentItemsList.setChildForceSaveFields(
			formModelItem,
			this.forceSaveFields,
		);
	}

	// Used in view
	private onRenderAdditionalInfo = (rowIndex: number): string => {
		const suffix = DynamicFieldTable.getSuffix(rowIndex + 1);
		const formModel = this.formModel.formModelList[rowIndex];
		let retInfo = '';
		if (!formModel || !formModel.fields) {
			return retInfo;
		}

		if (formModel.fields[MemberPaymentHistoryField.PYz_OrgName].value) {
			retInfo +=
				'<span class="additional-info additional-info__field additional-info__fund">' +
				formModel.fields[MemberPaymentHistoryField.PYz_OrgName].value +
				'</span>';
		}

		if (
			formModel.fields[MemberPaymentHistoryField.PYz_Aust_Business_No]
				.value
		) {
			retInfo +=
				'<span class="additional-info__title">ABN:</span> <span class="additional-info__field">' +
				formModel.fields[MemberPaymentHistoryField.PYz_Aust_Business_No]
					.value +
				'</span>';
		}

		if (formModel.fields[MemberPaymentHistoryField.PYz_ProdID].value) {
			retInfo +=
				'<span class="additional-info__title">USI:</span> <span class="additional-info__field">' +
				formModel.fields[MemberPaymentHistoryField.PYz_ProdID].value +
				'</span>';
		}

		if (
			formModel.fields[MemberPaymentHistoryField.PYz_ABN_Status]
				?.value === 'VC'
		) {
			retInfo +=
				'<span class="additional-info__abn-validated additional-info__field">***Validated***</span>';
		}

		return retInfo;
	};

	protected isPaymentItemPartOfList(formModelItem: FormModel, suffix) {
		return formModelItem.fields[this.uniqueFieldMeta + suffix]
			? true
			: false;
	}

	protected updateSelectedConvIdFilterOfFormModel() {
		const formModelList = this.formModel.formModelList;
		let tableRowIndex = 0;
		formModelList.forEach((childFormModel, listRowIndex) => {
			const suffix = DynamicFieldTable.getSuffix(listRowIndex + 1);
			if (this.isPaymentItemPartOfList(childFormModel, suffix)) {
				const conversationIdFieldModel: SearchFieldModel =
					childFormModel.fields[PaymentItemField.CONV_ID + suffix];
				if (conversationIdFieldModel) {
					AbstractPaymentItemsList.setupConvIdAdditionalFilters(
						conversationIdFieldModel,
						tableRowIndex,
						this.selectedConversationIds,
						this.formModel,
					);
				}
				tableRowIndex++;
			}
		});
	}

	protected updateConvIDField(
		listRowIndex: number,
		tableRowIndex: number,
		record,
	) {
		this.selectConvID(listRowIndex, record);
		if (record) {
			this.selectedConversationIds[tableRowIndex] = record.MSl_Record_No;
		} else {
			this.selectedConversationIds[tableRowIndex] = '';
		}
		this.updateSelectedConvIdFilterOfFormModel();
	}

	protected selectConvID(
		rowIndex: number,
		record: { [columnName: string]: any },
	) {
		const payeeFields: PayeeFields = {
			Abn: undefined,
			ProdId: undefined,
			Name: undefined,
			MemberId: undefined,
			Payee: undefined,
			Bsb: undefined,
			BankAccount: undefined,
			BankName: undefined,
			MS_REC_NO: undefined,
			Rollover: undefined,
		};
		if (record) {
			payeeFields.Abn = record.FQz_External_ABN;
			payeeFields.ProdId = record.FQz_External_ProdId;
			payeeFields.Name = record.FQz_External_OrgName;
			payeeFields.MemberId = record.FQz_ExternalMemberID;
			payeeFields.MS_REC_NO = String(record.MSl_Record_No);

			if (record.PRz_Code === null) {
				payeeFields.Payee = 'ROLLOV';
				payeeFields.Rollover = 'Y';
			} else {
				payeeFields.Payee = record.PRz_Code;
				payeeFields.Rollover = record.PRc_Rollover;
			}
			if (!payeeFields.ProdId) {
				payeeFields.Bsb = record.MSz_PayeeBSB;
				payeeFields.BankAccount = record.MSz_PayeeBankActNo;
				payeeFields.BankName = record.MSz_PayeeBankActName;
				payeeFields.Name = record.MSz_OrgName;
			}
		}
		this.formModel =
			AbstractPaymentItemsList.setPaymentItemFieldsAndGetFormModel(
				this.formModel,
				rowIndex,
				payeeFields,
			);
	}

	protected addMessageSubscription(
		childFormModel: FormModel,
		listRowIndex: number,
		tableRowIndex: number,
		suffix: string,
	) {
		const formModelFields = childFormModel.fields;
		const messageField = formModelFields[PaymentItemField.MSG + suffix];
		if (messageField) {
			const subscription = this.bindingEngine
				.propertyObserver(messageField, 'value')
				.subscribe((newValue) => {
					const conversationIdField =
						formModelFields[PaymentItemField.CONV_ID + suffix];
					const payeeField =
						formModelFields[PaymentItemField.PAYEE + suffix];
					const payByField =
						formModelFields[PaymentItemField.PAY_BY + suffix];
					AbstractPaymentItemsList.evaluateMessageField(
						newValue,
						conversationIdField,
						payeeField,
						payByField,
					);
					// Update conversation ID field after resetting its value
					this.updateConvIDField(
						listRowIndex,
						tableRowIndex,
						undefined,
					);
					// set messageField value
					if (!newValue.trim()) {
						messageField.value = MessageValue.NO;
					}
				});
			this.messageSubscriptions.push(subscription);
		}
	}

	protected static selectPayeeAndGetFormModel(
		formModel: FormModel,
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
	): FormModel {
		const payeeFields: PayeeFields = {
			Abn: undefined,
			ProdId: undefined,
			Name: undefined,
			MemberId: undefined,
			Payee: undefined,
			Bsb: undefined,
			BankAccount: undefined,
			BankName: undefined,
			MS_REC_NO: undefined,
			Rollover: undefined,
		};

		if (record) {
			payeeFields.Abn = record.PRz_Aust_Business_No.trim();
			payeeFields.ProdId = record.PRz_ProdID.trim();
			payeeFields.Rollover = record.PRc_Rollover;
			if (record.PRz_Code !== 'DEP') {
				payeeFields.Name = record.PRz_Name;
			}
			if (record.PRc_Type === 's' && record.PRv_RcvElServiceAddr) {
				payeeFields.Bsb = record.PRz_BSB;
				payeeFields.BankAccount = record.PRz_Bank_Acct_No;
				payeeFields.BankName = record.PRz_Bank_Acct_Name;
			}
		}

		return this.setPaymentItemFieldsAndGetFormModel(
			formModel,
			rowIndex,
			payeeFields,
		);
	}

	protected static setParentForceSaveFields(formModel: FormModel): void {
		formModel.fields.BSz_Fund.forceSave = true;
		formModel.fields.BSz_Member.forceSave = true;
		formModel.fields.BSd_Exit.forceSave = true;
		formModel.fields.BSl_Batch_Reference.forceSave = true;
	}

	protected static setChildForceSaveFields(
		formModel: FormModel,
		forceSaveFields,
	): void {
		forceSaveFields.forEach((field) => {
			formModel.fields[field].forceSave = true;
		});
	}

	protected static defaultValuesFromInsertedRow(
		rowId: number,
		formModelToInsert: FormModel,
		dynamicFieldMeta: DynamicFieldMeta[],
	): void {
		const suffix = DynamicFieldTable.getSuffix(rowId + 1);
		dynamicFieldMeta.forEach((fieldMeta) => {
			formModelToInsert.fields[fieldMeta.fieldNamePrefix + suffix].value =
				undefined;
			if (fieldMeta.fieldNamePrefix === PaymentItemField.PAYEE) {
				formModelToInsert.fields[
					fieldMeta.fieldNamePrefix + suffix
				].fieldDescription = undefined;
			}
		});

		formModelToInsert.fields[
			MemberPaymentHistoryField.PYz_ABN_Status
		].value = 'NR';
		formModelToInsert.fields[PaymentItemField.MSG + suffix].value = 'N';
		formModelToInsert.fields[PaymentItemField.CONV_ID + suffix].modifiable =
			Modifiable.FALSE;
		formModelToInsert.fields[PaymentItemField.PAYEE + suffix].modifiable =
			Modifiable.TRUE;
		formModelToInsert.fields[PaymentItemField.TOTAL_VALUE + suffix].value =
			'0.0';
		formModelToInsert.fields[
			PaymentItemField.PRESERVED_VALUE + suffix
		].value = '0.0';
		formModelToInsert.fields[PaymentItemField.PAY_BY + suffix].value = 'N';
		formModelToInsert.fields[MemberPaymentHistoryField.PYz_OrgName].value =
			'';
		formModelToInsert.fields[
			MemberPaymentHistoryField.PYz_Aust_Business_No
		].value = '';
		formModelToInsert.fields[
			MemberPaymentHistoryField.PYz_Product_Name
		].value = '';
		formModelToInsert.fields[MemberPaymentHistoryField.PYz_Payee].value =
			'';
		formModelToInsert.fields[PaymentItemField.MS_REC_NO].value = '';
		formModelToInsert.fields[PaymentItemField.CONV_ID + suffix].value = '';
	}

	private static setPaymentItemFieldsAndGetFormModel(
		formModel: FormModel,
		rowIndex: number,
		payeeFields: PayeeFields,
	): FormModel {
		const formModelList = formModel.formModelList;

		const suffix = DynamicFieldTable.getSuffix(rowIndex + 1);

		const rowFormModel = formModelList[rowIndex];

		rowFormModel.fields[MemberPaymentHistoryField.PYz_OrgName].value =
			payeeFields.Name;
		rowFormModel.fields[
			MemberPaymentHistoryField.PYz_Aust_Business_No
		].value = payeeFields.Abn;
		rowFormModel.fields[MemberPaymentHistoryField.PYz_ProdID].value =
			payeeFields.ProdId;
		rowFormModel.fields[MemberPaymentHistoryField.PYz_ABN_Status].value =
			'';
		rowFormModel.fields[MemberPaymentHistoryField.PYz_MemberID].value =
			payeeFields.MemberId;
		rowFormModel.fields[MemberPaymentHistoryField.PYz_Payee].value =
			payeeFields.Payee;
		rowFormModel.fields[PaymentItemField.PAYEE + suffix].value =
			payeeFields.Payee;
		rowFormModel.fields[MemberPaymentHistoryField.PYz_BSB].value =
			payeeFields.Bsb;
		rowFormModel.fields[MemberPaymentHistoryField.PYz_Bank_Acct_No].value =
			payeeFields.BankAccount;
		rowFormModel.fields[
			MemberPaymentHistoryField.PYz_Bank_Acct_Name
		].value = payeeFields.BankName;
		rowFormModel.fields[PaymentItemField.MS_REC_NO].value =
			payeeFields.MS_REC_NO;
		rowFormModel.fields[PaymentItemField.IS_PAYEE_ROLLOVER].value =
			payeeFields.Rollover;

		const updatedFormModel = FormModelUtils.cloneModel(formModel);
		updatedFormModel.formModelList = formModelList;
		return updatedFormModel;
	}

	protected static setupConvIdAdditionalFilters(
		conversationIdFieldModel: SearchFieldModel,
		rowId: number,
		selectedConversationIds: string[],
		formModel: FormModel,
	) {
		conversationIdFieldModel.additionalFilters = [
			{
				name: 'MSl_Record_No',
				value: selectedConversationIds
					.filter((value, index) => index !== rowId && value)
					.join(', '),
				operation: FilterType.IN,
				not: true,
			},
			{
				name: 'FQl_Batch_Reference',
				value: formModel.fields.BSl_Batch_Reference.value + ', 0',
				operation: FilterType.IN,
				not: false,
			},
		];
	}

	private static removeSubscriptions(subscriptions: Subscription[]) {
		subscriptions.forEach((subscription) => {
			subscription.dispose();
		});
	}

	protected static setupConvIdSearchField(
		conversationIdFieldModel: SearchFieldModel,
	) {
		conversationIdFieldModel.searchType =
			SearchType.BENEFITS_PAYMENTS_DISPERSALS_CONVERSATION_ID;
		conversationIdFieldModel.filterBy = [
			{
				col: BatchProcessFieldName.BSz_Fund,
				targetCol: 'FQz_Fund',
				ignoreBlank: true,
			},
			{
				col: BatchProcessFieldName.BSz_Member,
				targetCol: 'FQz_Member',
				ignoreBlank: true,
			},
		];
	}

	private static setupPayeeSearchField(payeeFormModel: SearchFieldModel) {
		payeeFormModel.searchType = SearchType.PAYEE;
		payeeFormModel.additionalFilters = [
			{
				name: 'PRc_Type',
				value: 'h',
				operation: FilterType.NOT_EQUALS,
				andJoin: true,
			},
			{
				name: 'PRc_Deleted',
				value: 'Y',
				operation: FilterType.NOT_EQUALS,
				andJoin: true,
			},
		];
	}

	private static addPayBySubscription(
		formModel: FormModel,
		suffix: string,
		bindingEngine: BindingEngine,
		payBySubscriptions: Subscription[],
	) {
		const payByField = formModel.fields[PaymentItemField.PAY_BY + suffix];
		if (payByField) {
			const subscription = bindingEngine
				.propertyObserver(payByField, 'value')
				.subscribe((newValue) => {
					if (!newValue.trim()) {
						payByField.value = 'N';
					}
				});
			payBySubscriptions.push(subscription);
		}
	}

	protected static evaluateMessageField(
		messageFieldValue: string,
		conversationIdFieldModel: FieldModel,
		payeeFieldModel: FieldModel,
		payByFieldMode: FieldModel,
	) {
		if (messageFieldValue === MessageValue.YES) {
			conversationIdFieldModel.modifiable = Modifiable.TRUE;
			conversationIdFieldModel.value = '';
			payeeFieldModel.modifiable = Modifiable.FALSE;
			payeeFieldModel.value = undefined;
			payByFieldMode.value = 'Y';
			payByFieldMode.modifiable = Modifiable.FALSE;
		} else {
			conversationIdFieldModel.modifiable = Modifiable.FALSE;
			conversationIdFieldModel.value = '';
			payeeFieldModel.modifiable = Modifiable.TRUE;
			conversationIdFieldModel.value = undefined;
			payByFieldMode.modifiable = Modifiable.TRUE;
		}
	}

	protected static removeRowAndGetFormModelList(
		rowIndex: number,
		totalRows: number,
		formModelList: FormModel[],
		dynamicFieldMeta: DynamicFieldMeta[],
	): FormModel[] {
		// decrement row id on all form model items after the removed form model
		for (let i = rowIndex + 1; i < totalRows; i++) {
			FormModelListFieldTable.updateRowId(
				formModelList[i].fields,
				PaymentItemField.PRIORITY,
				dynamicFieldMeta,
				i + 1,
				MoveRecordDirection.UP,
			);
		}
		// remove payment form model from list
		formModelList.splice(rowIndex, 1);

		return formModelList;
	}
}

export enum PaymentItemField {
	/* Custom fields not displayed in row and don't contain suffix */
	PRIORITY = 'ZYs_Priority',
	TOTAL_TYPE = 'ZYc_Total_Type',
	PRESERVED_TYPE = 'ZYc_Pres_Type',
	MS_REC_NO = 'ZZl_MS_REC_NO',
	/* Custom fields displayed in row and contain suffix e.g. ZYc_Msg01*/
	MSG = 'ZYc_Msg',
	CONV_ID = 'ZYz_Initiate_Conv_ID',
	PAYEE = 'ZYz_Payee',
	TOTAL_VALUE = 'ZYf_Total_Value',
	PRESERVED_VALUE = 'ZYf_Pres_Value',
	PAY_BY = 'ZYc_Pay_By',
	DISPERSAL_LINE = 'ZYc_Dispersal_Line',
	PAYMENT_DETAIL_LINE = 'ZYc_Payment_Detail_Line',
	IS_PAYEE_ROLLOVER = 'ZYc_Is_Payee_Rollover',
}

interface PayeeFields {
	Abn: string;
	ProdId: string;
	Name: string;
	MemberId: string;
	Payee: string;
	Bsb: string;
	BankAccount: string;
	BankName: string;
	MS_REC_NO: string;
	Rollover: string;
}

export enum MessageValue {
	YES = 'Y',
	NO = 'N',
}
